/**
 * CascaAdmin — a casca de navegação das ferramentas internas da Use Telecom (padrão A + F).
 *
 * Junta as peças: barra lateral (≥ 1024 px) OU gaveta modal (< 1024 px), barra superior fixa de 64 px,
 * skip link, `<main id="conteudo">`, busca de telas, tema, "Sem acesso" e limite de erro. Não sabe nada do
 * produto: recebe o REGISTRO de telas (`itens`/`grupos`), o papel, o usuário e as funções (`aoSair`,
 * `renderLink`, `aoIrPara`). Ver `README.md` para a API e para copiar a pasta em outro sistema.
 *
 * Responsabilidades que ficam aqui e não nas telas (e por quê):
 *  - `document.title` = "<Tela> · <Sistema>" a cada rota (antes: o mesmo título nas 38 rotas; NAV-16).
 *  - Depois de NAVEGAR, o foco vai ao `<main>` (não no primeiro carregamento: ali o 1º Tab tem de cair no
 *    skip link). Sem isso o foco ficava num link do menu que acabou de sair da tela.
 *  - Fundo `inert` com a gaveta ou a busca abertas: `aria-modal` sozinho não impede o leitor de tela de
 *    ler o fundo.
 *  - Atalhos `/` e Ctrl+K abrem "Ir para…" (nunca dentro de campo de texto).
 *  - O diálogo (gaveta/busca) fecha no CLIQUE, antes da rota mudar: assim o foco devolvido ao botão de
 *    origem acontece ANTES do foco ir ao `<main>`, e não depois (a ordem inversa deixava o foco no botão).
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { BarraSuperior } from "./BarraSuperior.js";
import { BuscaDeTelas } from "./BuscaDeTelas.js";
import { Gaveta } from "./Gaveta.js";
import { MarcaDoSistema } from "./MarcaDoSistema.js";
import { NavLateral } from "./NavLateral.js";
import { SeletorDeTema } from "./SeletorDeTema.js";
import { ErroDeTela } from "./ErroDeTela.js";
import { LogOut } from "lucide-react";
import { SemAcesso } from "./SemAcesso.js";
import {
  itemAtivo, itensVisiveis, LIMITE_ACORDEAO, LIMITE_BUSCA, podeAbrir, temBuscaDeTelas, tituloDaAba, trilhaDe,
} from "./registro.js";
import type { GrupoNav, ItemNav, PreferenciaTema, ProdutoShell, RenderLinkShell, UsuarioShell } from "./tipos.js";
import { ehCampoDeTexto, useMidia } from "./util.js";

export interface CascaAdminProps {
  produto: ProdutoShell;
  grupos: readonly GrupoNav[];
  /** O registro INTEIRO (inclusive o que o papel não abre: é dele que sai o "Sem acesso"). */
  itens: readonly ItemNav[];
  /** Papel da pessoa logada (`null` = sessão sem papel: só vê o que não exige papel). */
  papel: string | null;
  /** Nomes dos papéis para o "Sem acesso" ("master" → "Master"). */
  rotuloDoPapel: (papel: string) => string;
  usuario: UsuarioShell | null;
  pathname: string;
  preferenciaTema: PreferenciaTema;
  aoMudarTema: (p: PreferenciaTema) => void;
  aoSair: () => void;
  renderLink: RenderLinkShell;
  /** Navegar sem recarregar (busca "Ir para…"). */
  aoIrPara: (href: string) => void;
  /** Sino/avisos na barra superior, se houver. */
  avisos?: ReactNode;
  limiteAcordeao?: number;
  limiteBusca?: number;
  /**
   * Rotas que NÃO passam pelo "Sem acesso" mesmo com o item bloqueado (a home que redireciona por papel,
   * ex.: o Financeiro cai em /admin e é levado a Circuitos).
   */
  rotasLivres?: readonly string[];
  children: ReactNode;
}

export function CascaAdmin(p: CascaAdminProps) {
  const {
    produto, grupos, itens, papel, usuario, pathname, preferenciaTema, aoMudarTema, aoSair, renderLink, aoIrPara,
    limiteAcordeao = LIMITE_ACORDEAO, limiteBusca = LIMITE_BUSCA, rotasLivres = [],
  } = p;
  const idGaveta = useId();
  const ehDesktop = useMidia("(min-width: 1024px)");
  const [gavetaAberta, setGavetaAberta] = useState(false);
  const [buscaAberta, setBuscaAberta] = useState(false);
  const botaoMenuRef = useRef<HTMLButtonElement>(null);
  const fundoRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const rotaAnterior = useRef(pathname);

  const visiveis = itensVisiveis(itens, papel);
  const ativo = itemAtivo(itens, pathname);
  const bloqueado = !!ativo && !podeAbrir(ativo, papel) && !rotasLivres.includes(pathname);
  const comBusca = temBuscaDeTelas(visiveis.length, limiteBusca);

  const fecharGaveta = useCallback(() => setGavetaAberta(false), []);
  const fecharBusca = useCallback(() => setBuscaAberta(false), []);

  // Viraram desktop com a gaveta aberta: fecha (a barra lateral assume).
  useEffect(() => { if (ehDesktop) setGavetaAberta(false); }, [ehDesktop]);
  // Rede de segurança: rota mudou por outro caminho que não o clique do menu.
  useEffect(() => { setGavetaAberta(false); setBuscaAberta(false); }, [pathname]);

  // Título da aba por rota.
  useEffect(() => {
    document.title = bloqueado && ativo ? `Sem acesso · ${produto.sufixoDoTitulo}` : tituloDaAba(ativo, produto.sufixoDoTitulo);
  }, [ativo, bloqueado, produto.sufixoDoTitulo]);

  // Foco no conteúdo depois de navegar. Compara com a rota ANTERIOR (e não com um "já rodou"): no modo
  // estrito do React o efeito roda duas vezes na montagem, e a 2ª puxava o foco para o `<main>` já na
  // abertura da página — o 1º Tab pulava o skip link.
  useEffect(() => {
    if (rotaAnterior.current === pathname) return;
    rotaAnterior.current = pathname;
    mainRef.current?.focus({ preventScroll: true });
  }, [pathname]);

  // Fundo inerte enquanto há diálogo aberto. ⚠️ `useLayoutEffect` DE PROPÓSITO: ao fechar, o foco é
  // devolvido ao botão de origem pelo cleanup (passivo) do diálogo; se o fundo ainda estiver `inert` nesse
  // momento, `focus()` é ignorado e o foco cai no <body> (medido em 11/10/2026). O layout effect tira o
  // `inert` ANTES dos efeitos passivos rodarem.
  const modalAberto = gavetaAberta || buscaAberta;
  useLayoutEffect(() => {
    const el = fundoRef.current as (HTMLElement & { inert?: boolean }) | null;
    if (el) el.inert = modalAberto;
  }, [modalAberto]);

  // Atalhos "/" e Ctrl/⌘+K.
  useEffect(() => {
    if (!comBusca) return;
    const aoTeclar = (e: KeyboardEvent) => {
      const ctrlK = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k";
      const barra = e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey && !ehCampoDeTexto(e.target);
      if (ctrlK || barra) { e.preventDefault(); setGavetaAberta(false); setBuscaAberta(true); }
    };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [comBusca]);

  const trilha = bloqueado && ativo ? { tela: "Sem acesso" } : ativo ? trilhaDe(ativo, grupos) : { tela: "Tela não encontrada" };

  const marca = <MarcaDoSistema produto={produto} renderLink={renderLink} aoClicar={fecharGaveta} />;
  const menu = (
    <NavLateral
      itens={visiveis}
      grupos={grupos}
      ativoId={bloqueado ? null : ativo?.id ?? null}
      renderLink={renderLink}
      aoNavegar={fecharGaveta}
      limiteAcordeao={limiteAcordeao}
      className="casca-menu"
    />
  );

  const primeiraTela = visiveis[0] ?? null;
  const quemAbre = ativo?.papeis ? ativo.papeis.map(p.rotuloDoPapel).join(", ") : "";

  return (
    <div className="casca">
      <a className="pular" href="#conteudo">Pular para o conteúdo</a>

      <div ref={fundoRef} className="casca-fundo">
        {ehDesktop && (
          <aside className="casca-trilho">
            <div className="casca-trilho-topo">{marca}</div>
            {menu}
          </aside>
        )}

        <div className="casca-coluna">
          <BarraSuperior
            trilha={trilha}
            comBotaoMenu={!ehDesktop}
            menuAberto={gavetaAberta}
            idDaGaveta={idGaveta}
            aoAbrirMenu={() => setGavetaAberta(true)}
            botaoMenuRef={botaoMenuRef}
            comBusca={comBusca}
            aoAbrirBusca={() => setBuscaAberta(true)}
            avisos={p.avisos}
            preferenciaTema={preferenciaTema}
            aoMudarTema={aoMudarTema}
            usuario={usuario}
            aoSair={aoSair}
          />
          <main id="conteudo" ref={mainRef} tabIndex={-1} className="casca-main">
            <div className="casca-conteudo">
              <ErroDeTela resetKey={pathname} renderLink={renderLink} hrefInicio={produto.href}>
                {bloqueado && ativo ? (
                  <SemAcesso
                    rotulo={ativo.rotulo}
                    papelRotulo={usuario?.papelRotulo ?? (papel ? p.rotuloDoPapel(papel) : "sem perfil")}
                    quemAbre={quemAbre}
                    destino={primeiraTela ? { href: primeiraTela.href, rotulo: primeiraTela.rotulo } : null}
                    renderLink={renderLink}
                  />
                ) : (
                  p.children
                )}
              </ErroDeTela>
            </div>
          </main>
        </div>
      </div>

      {!ehDesktop && (
        <Gaveta
          id={idGaveta}
          aberta={gavetaAberta}
          aoFechar={fecharGaveta}
          marca={marca}
          conta={
            <>
              {usuario && (
                <div className="casca-conta-pessoa">
                  <span className="casca-pessoa-linha1">{usuario.nome}</span>
                  {usuario.email && <span className="casca-pessoa-linha2">{usuario.email}</span>}
                  <span className="casca-papel">{usuario.papelRotulo}</span>
                </div>
              )}
              <SeletorDeTema preferencia={preferenciaTema} aoMudar={aoMudarTema} variante="cheia" />
              <button type="button" className="casca-btn casca-sair casca-sair-cheio" onClick={aoSair}>
                <LogOut size={18} aria-hidden="true" />
                <span>Sair</span>
              </button>
            </>
          }
        >
          {menu}
        </Gaveta>
      )}

      {comBusca && (
        <BuscaDeTelas
          aberta={buscaAberta}
          aoFechar={fecharBusca}
          itens={visiveis}
          grupos={grupos}
          aoEscolher={(item) => { setBuscaAberta(false); aoIrPara(item.href); }}
        />
      )}
    </div>
  );
}
